import React from 'react';
import { PiggyBank, Target, TrendingUp, Shield, Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const RetirementPlanningPage = () => {
  const services = [
    {
      icon: Target,
      title: 'Retirement Goal Setting',
      description: 'Define your retirement goals and calculate how much you need to save to achieve the lifestyle you want in your golden years.'
    },
    {
      icon: PiggyBank,
      title: 'Savings Strategies',
      description: 'Develop effective savings strategies and contribution plans to build your retirement nest egg systematically over time.'
    },
    {
      icon: TrendingUp,
      title: 'Investment Planning',
      description: 'Strategic investment planning for retirement accounts including 401(k), IRAs, and other retirement vehicles to maximize growth potential.'
    },
    {
      icon: Shield,
      title: 'Risk Management',
      description: 'Protect your retirement savings with appropriate risk management strategies and asset allocation as you approach retirement age.'
    },
    {
      icon: Calendar,
      title: 'Withdrawal Planning',
      description: 'Plan for retirement income and develop withdrawal strategies to ensure your savings last throughout your retirement years.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/senior-4723737_640.jpg"
            alt="Retirement Planning"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Retirement Planning</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Services</span> / <span className="text-gold-400">Retirement Planning</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Secure Your <span className="text-gold-600">Retirement Future</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Secure your future with our retirement planning solutions. We help you build a nest egg that ensures financial independence and peace of mind during your golden years.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our comprehensive retirement planning services include goal setting, savings strategies, investment planning, and withdrawal strategies. We work with you to create a personalized retirement plan that aligns with your timeline, risk tolerance, and retirement aspirations.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="/images/senior-4723737_640.jpg"
                  alt="Retirement Planning Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Plan for Retirement?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Start building your retirement nest egg today with expert guidance and personalized planning. 
              Secure your financial future and enjoy peace of mind in your golden years.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full transition-colors duration-300"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetirementPlanningPage;

